// every failure the apps can meet, as one typed error with a plain-English message

export type ErrorKind =
  | 'offline'
  | 'network'
  | 'timeout'
  | 'rate_limited'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'auth'
  | 'unknown';

type ClientErrorInit = {
  kind: ErrorKind;
  code?: string;
  status?: number;
  requestId?: string;
  fields?: Record<string, string>;
  serverMessage?: string;
};

// the base error the API client and the sign-in provider throw; screens read it through describeError
export class ClientError extends Error {
  readonly kind: ErrorKind;
  readonly code?: string;
  readonly status?: number;
  readonly requestId?: string;
  readonly fields?: Record<string, string>;
  readonly serverMessage?: string;
  constructor(init: ClientErrorInit) {
    super(init.serverMessage ?? init.code ?? init.kind);
    this.name = 'ClientError';
    this.kind = init.kind;
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.fields = init.fields;
    this.serverMessage = init.serverMessage;
  }
}

// what a screen shows: one message, whether Retry makes sense, field messages and the support reference
export type FriendlyError = {
  kind: ErrorKind;
  code?: string;
  message: string;
  retryable: boolean;
  requestId?: string;
  fields?: Record<string, string>;
};

// the message for each kind when nothing more specific is known
export const KIND_MESSAGES: Record<ErrorKind, string> = {
  offline: 'You appear to be offline. Check your connection and try again.',
  network: 'We could not reach London Life. Check your connection and try again.',
  timeout: 'This is taking longer than it should. Please try again.',
  rate_limited: 'You are going a bit fast. Wait a moment and try again.',
  unauthorized: 'Your session has ended. Please sign in again.',
  forbidden: 'You do not have access to do that.',
  not_found: 'We could not find that. It may have been moved or removed.',
  conflict: 'That has changed or already exists. Refresh and try again.',
  validation: 'Some details need fixing. Check the highlighted fields.',
  server: 'Something went wrong on our side. Please try again in a moment.',
  auth: 'Sign-in did not work. Please try again.',
  unknown: 'Something went wrong. Please try again.',
};

// sign-in provider codes (Firebase today) in plain English
export const AUTH_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'That email or password is not right.',
  'auth/user-not-found': 'That email or password is not right.',
  'auth/wrong-password': 'That email or password is not right.',
  'auth/invalid-email': 'That email address does not look right.',
  'auth/missing-password': 'Enter your password.',
  'auth/weak-password': 'Choose a password with at least 8 characters.',
  'auth/email-already-in-use': 'An account already uses that email. Sign in instead, or reset your password.',
  'auth/user-disabled': 'This account has been disabled. Contact support if you think this is a mistake.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again, or reset your password.',
  'auth/popup-closed-by-user': 'The Google window was closed before sign-in finished.',
  'auth/cancelled-popup-request': 'The Google window was closed before sign-in finished.',
  'auth/popup-blocked': 'Your browser blocked the Google window. Allow pop-ups for this site and try again.',
  'auth/network-request-failed': KIND_MESSAGES.network,
  'auth/unauthorized-domain': 'Sign-in is not available on this web address. Use the official London Life site.',
  'auth/requires-recent-login': 'For your safety, sign in again before doing that.',
  'auth/email-not-verified': 'Verify your email address first. Check your inbox for the link.',
};

const RETRYABLE: ReadonlySet<ErrorKind> = new Set(['offline', 'network', 'timeout', 'rate_limited', 'server']);

// API error codes map to kinds; kinds decide the message unless the API wrote one for people
export function kindForApiCode(code: string): ErrorKind {
  switch (code) {
    case 'bad_request':
      return 'validation';
    case 'unauthorized':
      return 'unauthorized';
    case 'forbidden':
    case 'two_step_setup_required':
    case 'two_step_required':
    case 'profile_required':
    case 'age_restricted':
    case 'account_held':
    case 'account_frozen':
      return 'forbidden';
    case 'insufficient_funds':
    case 'idempotency_conflict':
      return 'conflict';
    case 'not_found':
    case 'conflict':
    case 'rate_limited':
      return code;
    case 'internal':
      return 'server';
    default:
      return 'unknown';
  }
}

// the API writes its 4xx messages for people (forbidden, not found, conflict, validation), so those are
// shown as they are; everything else uses the catalogue so no technical text reaches the screen
const SERVER_MESSAGE_KINDS: ReadonlySet<ErrorKind> = new Set([
  'forbidden',
  'not_found',
  'conflict',
  'validation',
  'rate_limited',
]);

export function describeError(error: unknown): FriendlyError {
  if (error instanceof ClientError) {
    const message =
      (error.kind === 'auth' && error.code && AUTH_MESSAGES[error.code]) ||
      (SERVER_MESSAGE_KINDS.has(error.kind) && error.serverMessage) ||
      KIND_MESSAGES[error.kind];
    return {
      kind: error.kind,
      code: error.code,
      message,
      retryable: RETRYABLE.has(error.kind),
      requestId: error.requestId,
      fields: error.fields,
    };
  }
  return { kind: 'unknown', message: KIND_MESSAGES.unknown, retryable: true };
}
